import { migrateTableColumnPrefs } from "@/lib/utils/tanstack-table-local-storage";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type GroupingState,
  type PaginationState,
  type Row,
  type SortingFn,
  type SortingState,
  type VisibilityState,
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Button,
  LoadingSpinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@/components/ui";
import { DataTableCellText } from "@/components/ui/shadcn/DataTable/DataTableCellText";
import { difference, isNil } from "lodash";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { DataTablePagination } from "./DataTablePagination";
import { DataTableVisibilityDropdown } from "./DataTableVisibilityDropdown";

import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";

import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

// needed for row & cell level scope DnD setup
import { DataTableFiltersToolbar } from "@/components/ui/shadcn/DataTable/DataTableFiltersToolbar";
import { DragAlongCell } from "@/components/ui/shadcn/DataTable/DragAlongCell";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { type tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { cn } from "@/lib/utils/cn";
import {
  buildReportCsvContent,
  downloadReportCsvFile,
  stringifyReportCsvValue,
} from "@/lib/utils/reports/report-csv";
import { type Updater } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronDownIcon, ChevronRightIcon, Download } from "lucide-react";
import toast from "react-hot-toast";
import { DraggableTableHeader } from "./DraggableTableHeader";

declare module "@tanstack/table-core" {
  interface SortingFns {
    customDateTime: SortingFn<unknown>;
  }
}

export type DataTableChrome = "default" | "matterDashboard";

export type DataTableColumnMeta = {
  cellClassName?: string;
  headerAlign?: "start" | "end";
  preserveHeaderLabel?: boolean;
};

export type DataTableServerPagination = {
  pagination: PaginationState;
  onPaginationChange: (
    updater: Updater<PaginationState, PaginationState>,
  ) => void;
  pageCount: number;
  rowCount?: number;
  getCanNextPage?: boolean;
};

export type DataTableServerSorting = {
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState, SortingState>) => void;
};

interface DataTableProps<TData, TValue> {
  allowLocalStorage?: boolean;
  columnIdAliases?: Record<string, string>;
  newColumnAnchors?: Record<string, string>;
  /** Matches My Matters dashboard card: rounded shell, muted toolbar strip, table/pagination chrome. */
  chrome?: DataTableChrome;
  /** Plural label for the pagination summary (e.g. "matters"). */
  entityLabelPlural?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  grouping?: GroupingState;
  hidePagination?: boolean;
  hideSearchBar?: boolean;
  hideVisiibilityDropdown?: boolean;
  leftToolbarContent?: React.ReactNode;
  toolbarFilters?: React.ReactNode;
  initialSort?: { id: string; desc: boolean };
  initialVisibility?: VisibilityState;
  isError?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
  noResultsMessage?: string;
  noResultsContent?: React.ReactNode;
  onColumnOrderChange?: (order: string[]) => void;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  onRowClick?: (row: Row<TData>) => void;
  renderRowContextMenu?: (row: Row<TData>, trigger: ReactNode) => ReactNode;
  renderFooterRow?: (visibleColumnIds: string[]) => ReactNode;
  tableButtons?: React.ReactNode;
  tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
  autoResetPageIndex?: boolean;
  exportCsv?: {
    fileName: string;
    getCellValue: (row: TData, columnId: string) => unknown;
  };
  /** Merged with default bordered wrapper around the scrollable table. */
  tableContainerClassName?: string;
  /** Merged with default `<table>` classes (e.g. `bg-white` instead of `bg-card`). */
  tableElementClassName?: string;
  /** Merged with default header `<tr>` classes (e.g. plain white header row). */
  headerRowClassName?: string;
  /** Merged with default body row classes (e.g. hover style overrides). */
  bodyRowClassName?: string;
  serverPagination?: DataTableServerPagination;
  serverSorting?: DataTableServerSorting;
  getRowId?: (originalRow: TData, index: number) => string;
}

export function DataTable<TData, TValue>({
  allowLocalStorage = true,
  columnIdAliases = {},
  newColumnAnchors = {},
  chrome = "default",
  entityLabelPlural = "rows",
  columns,
  data,
  grouping,
  hidePagination = false,
  leftToolbarContent,
  toolbarFilters,
  hideSearchBar,
  hideVisiibilityDropdown,
  initialSort,
  initialVisibility,
  isError,
  isFetching,
  isLoading,
  noResultsMessage,
  noResultsContent,
  onColumnOrderChange,
  onColumnVisibilityChange,
  onRowClick,
  renderRowContextMenu,
  renderFooterRow,
  tableButtons,
  tableName,
  autoResetPageIndex = true,
  exportCsv,
  tableContainerClassName,
  tableElementClassName,
  headerRowClassName,
  bodyRowClassName,
  serverPagination,
  serverSorting,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const isMatterDashboardChrome = chrome === "matterDashboard";

  const localStorageObject = allowLocalStorage
    ? window.localStorage.getItem(tableName)
    : null;
  const localStorageJSON:
    | {
        columnOrder?: string[];
        columnVisibility?: VisibilityState;
        pagination?: PaginationState;
        sorting?: SortingState;
      }
    | undefined = localStorageObject
    ? migrateTableColumnPrefs(
        JSON.parse(localStorageObject),
        columnIdAliases,
        newColumnAnchors,
      )
    : undefined;
  const [sorting, setSorting] = useState<SortingState>(
    localStorageJSON?.sorting
      ? localStorageJSON.sorting
      : initialSort
        ? [initialSort]
        : [],
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: localStorageJSON?.pagination?.pageSize ?? 10,
  });
  const tablePagination = serverPagination?.pagination ?? pagination;
  const tableSorting = serverSorting?.sorting ?? sorting;
  // This is required so that if a user has set a column as hidden and later we set it as unhideable, it will always appear
  const unHidableColumns = columns
    .filter((column) => {
      return column.enableHiding === false;
    })
    .reduce<VisibilityState>((acc, column) => {
      return {
        ...acc,
        [column.id as string]: true,
      };
    }, {});

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    localStorageJSON?.columnVisibility
      ? {
          ...initialVisibility,
          ...localStorageJSON.columnVisibility,
          ...unHidableColumns,
        }
      : {
          ...initialVisibility,
          ...unHidableColumns,
        },
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string | undefined>();
  const tableCols = columns.map((c) => c.id || "");
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    localStorageJSON?.columnOrder
      ? [
          ...localStorageJSON.columnOrder,
          ...difference(tableCols, localStorageJSON.columnOrder),
        ]
      : tableCols,
  );

  const handleColumnSortingChange = (
    updater: Updater<SortingState, SortingState>,
  ) => {
    const updatedColumn = functionalUpdate(updater, tableSorting);
    if (allowLocalStorage) {
      window.localStorage.setItem(
        tableName,
        JSON.stringify({ ...localStorageJSON, sorting: updatedColumn }),
      );
    }
    if (serverSorting) {
      serverSorting.onSortingChange(updatedColumn);
    } else {
      setSorting(updatedColumn);
    }
    const resetPagination = {
      pageIndex: 0,
      pageSize: tablePagination.pageSize,
    };
    if (serverPagination) {
      serverPagination.onPaginationChange(resetPagination);
    } else {
      setPagination(resetPagination);
    }
  };

  const handleColumnOrderChange = (updater: Updater<string[], string[]>) => {
    const updatedColumn = functionalUpdate(updater, columnOrder);
    if (allowLocalStorage) {
      window.localStorage.setItem(
        tableName,
        JSON.stringify({ ...localStorageJSON, columnOrder: updatedColumn }),
      );
    }
    onColumnOrderChange?.(updatedColumn);
    setColumnOrder(updater);
  };

  const handleColumnVisibilityChange = (
    updater: Updater<VisibilityState, VisibilityState>,
  ) => {
    const updatedColumn = functionalUpdate(updater, columnVisibility);
    if (allowLocalStorage) {
      window.localStorage.setItem(
        tableName,
        JSON.stringify({
          ...localStorageJSON,
          columnVisibility: updatedColumn,
        }),
      );
    }
    onColumnVisibilityChange?.(updatedColumn);
    setColumnVisibility(updater);
  };

  const handlePaginationChange = (
    updater: Updater<PaginationState, PaginationState>,
  ) => {
    const updatedColumn = functionalUpdate(updater, tablePagination);
    if (allowLocalStorage) {
      window.localStorage.setItem(
        tableName,
        JSON.stringify({
          ...localStorageJSON,
          pagination: { pageSize: updatedColumn.pageSize },
        }),
      );
    }
    if (serverPagination) {
      serverPagination.onPaginationChange(updater);
    } else {
      setPagination(updater);
    }
  };

  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    autoResetExpanded: false,
    autoResetPageIndex: serverPagination ? false : autoResetPageIndex,
    manualPagination: Boolean(serverPagination),
    manualSorting: Boolean(serverSorting),
    pageCount: serverPagination?.pageCount,
    rowCount: serverPagination?.rowCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: hidePagination ? undefined : getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleColumnSortingChange,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    getGroupedRowModel: grouping ? getGroupedRowModel() : undefined,
    getRowId,
    globalFilterFn: "includesString",
    sortingFns: {
      customDateTime: (a, b, columnId) => {
        if (isNil(a.getValue(columnId))) {
          return 1;
        }
        if (isNil(b.getValue(columnId))) {
          return -1;
        }
        return dayjs(a.getValue(columnId)).diff(dayjs(b.getValue(columnId)));
      },
    },
    state: {
      columnFilters,
      columnVisibility,
      columnOrder,
      grouping: grouping ?? [],
      globalFilter,
      pagination: tablePagination,
      sorting: tableSorting,
    },
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      handleColumnOrderChange((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  useEffect(() => {
    table.toggleAllRowsExpanded(true);
  }, [table]);

  const exportRows = table.getPrePaginationRowModel().rows;
  const visibleExportColumns = table
    .getVisibleLeafColumns()
    .filter((column) => Boolean(column.id));
  const headerContent = (
    <TableHeader
      className={cn("relative z-20", isMatterDashboardChrome && "bg-muted/40")}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow
          key={headerGroup.id}
          className={cn(
            "table-row",
            isMatterDashboardChrome
              ? "border-border text-foreground border-b"
              : "bg-light-primary text-dark-primary hover:bg-light-primary",
            headerRowClassName,
          )}
        >
          {headerGroup.headers.map((header) => {
            return header.column.getIsGrouped() ? (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ) : (
              <SortableContext
                key={header.id}
                items={columnOrder}
                strategy={horizontalListSortingStrategy}
              >
                <DraggableTableHeader header={header} />
              </SortableContext>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
  const rowCount = table.getRowModel().rows.length;
  const loadingBodyHeight = hidePagination
    ? 112
    : Math.max(112, table.getState().pagination.pageSize * 40);
  const shouldRenderStandaloneEmptyState =
    !isError && !isLoading && rowCount === 0 && Boolean(noResultsContent);
  const canExportCsv =
    Boolean(exportCsv) &&
    !isLoading &&
    !isError &&
    visibleExportColumns.length > 0 &&
    exportRows.length > 0;

  const handleExportCsv = () => {
    if (!exportCsv) {
      return;
    }

    try {
      const csvContent = buildReportCsvContent({
        headers: visibleExportColumns.map((column) => column.id),
        rows: exportRows.map((row) =>
          visibleExportColumns.map((column) =>
            stringifyReportCsvValue(
              exportCsv.getCellValue(row.original, column.id),
            ),
          ),
        ),
      });

      downloadReportCsvFile({
        content: csvContent,
        fileName: exportCsv.fileName,
      });
    } catch (error) {
      console.error("CSV export failed", error);
      toast.error("Unable to export CSV.");
    }
  };

  const toolbarTrailing = (
    <>
      {!hideVisiibilityDropdown && (
        <DataTableVisibilityDropdown table={table} />
      )}
      {exportCsv ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 shrink-0"
          disabled={!canExportCsv}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleExportCsv();
          }}
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      ) : null}
      {tableButtons}
    </>
  );

  const paginationContent =
    !shouldRenderStandaloneEmptyState && !hidePagination ? (
      <div
        className={cn(
          "border-border shrink-0 border-t px-4 py-3",
          !isMatterDashboardChrome && "bg-card rounded-b-md",
        )}
      >
        <DataTablePagination
          table={table}
          entityLabelPlural={entityLabelPlural}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          isServerPaginated={Boolean(serverPagination)}
          serverRowCount={serverPagination?.rowCount}
          serverCanNextPage={serverPagination?.getCanNextPage}
        />
      </div>
    ) : null;

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div
        className={cn(
          isMatterDashboardChrome
            ? "border-border bg-card w-full min-w-0 rounded-xl border shadow-xs"
            : "w-full max-w-full",
        )}
      >
        {toolbarFilters ? (
          <div className="@container">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 py-2 @[70rem]:grid-cols-[auto_minmax(0,1fr)_auto]">
              <div className="min-w-0">{leftToolbarContent}</div>
              <div className="col-start-2 row-start-1 flex flex-wrap items-center justify-end gap-2 @[70rem]:col-start-3">
                {toolbarTrailing}
              </div>
              <div className="col-span-2 min-w-0 @[70rem]:col-span-1 @[70rem]:col-start-2 @[70rem]:row-start-1">
                {toolbarFilters}
              </div>
            </div>
          </div>
        ) : isMatterDashboardChrome ? (
          <div className="border-border bg-muted/30 flex min-w-0 flex-wrap items-end justify-between gap-3 border-b px-4 py-3">
            <div className="min-w-0 flex-1">
              {leftToolbarContent ?? (
                <DataTableFiltersToolbar
                  table={table}
                  hideSearchBar={hideSearchBar}
                />
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 self-end">
              {toolbarTrailing}
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 flex-wrap items-end justify-between gap-2">
            <div className="min-w-0 flex-1">
              {leftToolbarContent ?? (
                <DataTableFiltersToolbar
                  table={table}
                  hideSearchBar={hideSearchBar}
                />
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 self-end">
              {toolbarTrailing}
            </div>
          </div>
        )}
        <div className="relative">
          {isFetching && !isLoading && (
            <div className="bg-background/75 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
              <LoadingSpinner />
            </div>
          )}
          <div
            className={cn(
              isMatterDashboardChrome
                ? "w-full min-w-0 overflow-x-auto overscroll-x-contain"
                : "border-border mt-2 rounded-md border",
              tableContainerClassName,
            )}
          >
            <Table
              className={cn(
                "bg-card table",
                isMatterDashboardChrome
                  ? "w-max min-w-full rounded-none border-0"
                  : "rounded-md",
                tableElementClassName,
              )}
              data-testid={tableName}
              data-table-name={tableName}
            >
              {shouldRenderStandaloneEmptyState ? (
                <>
                  {headerContent}
                  <TableBody />
                </>
              ) : isError ? (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="table-cell h-24 text-center"
                    >
                      <Text variant="caption" color="destructive">
                        {serverErrorFallbackText}
                      </Text>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : isLoading ? (
                <>
                  {headerContent}
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="table-cell justify-items-center"
                        style={{ height: loadingBodyHeight }}
                      >
                        <LoadingSpinner />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </>
              ) : (
                <>
                  {headerContent}
                  <TableBody>
                    {rowCount ? (
                      table.getRowModel().rows.map((row) => {
                        const trigger = (
                          <TableRow
                            key={row.id}
                            className={cn(
                              "table-row",
                              onRowClick && "clickable cursor-pointer",
                              isMatterDashboardChrome &&
                                "border-border hover:bg-muted/20 border-b",
                              bodyRowClassName,
                            )}
                            data-testid="table-row"
                            data-state={row.getIsSelected() && "selected"}
                            onClick={() => onRowClick?.(row)}
                          >
                            {row.getVisibleCells().map((cell) => {
                              const tableCellClassName = cn(
                                isMatterDashboardChrome
                                  ? "py-3 pr-4 pl-10 first:pl-10"
                                  : "py-1 pr-1 pl-10 first:pl-10",
                                cell.getIsGrouped() &&
                                  (isMatterDashboardChrome
                                    ? "bg-muted/30 hover:bg-muted/30"
                                    : "bg-light-primary/50 hover:bg-light-primary/50"),
                                cell.getIsAggregated() &&
                                  (isMatterDashboardChrome
                                    ? "bg-muted/30 hover:bg-muted/30"
                                    : "bg-light-primary/50 hover:bg-light-primary/50"),
                              );
                              return (
                                <SortableContext
                                  key={cell.id}
                                  items={columnOrder}
                                  strategy={horizontalListSortingStrategy}
                                >
                                  {cell.getIsGrouped() ? (
                                    <TableCell className={tableCellClassName}>
                                      <span className="flex items-center gap-x-2">
                                        <Button
                                          variant="link"
                                          className={cn(
                                            "flex items-center gap-x-1 border border-transparent pr-1 whitespace-nowrap transition-all hover:border-transparent",
                                            row.getCanExpand()
                                              ? "cursor-pointer"
                                              : "cursor-default",
                                          )}
                                          {...{
                                            onClick:
                                              row.getToggleExpandedHandler(),
                                          }}
                                        >
                                          {row.getIsExpanded() ? (
                                            <ChevronDownIcon className="h-4 w-4" />
                                          ) : (
                                            <ChevronRightIcon className="h-4 w-4" />
                                          )}{" "}
                                          {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                          )}{" "}
                                          ({row.subRows.length})
                                        </Button>
                                      </span>
                                    </TableCell>
                                  ) : cell.getIsAggregated() ? (
                                    <TableCell className={tableCellClassName}>
                                      <DataTableCellText
                                        canHide={cell.column.getCanHide()}
                                      >
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext(),
                                        )}
                                      </DataTableCellText>
                                    </TableCell>
                                  ) : cell.getIsPlaceholder() ? (
                                    <TableCell className={tableCellClassName} />
                                  ) : (
                                    <DragAlongCell
                                      cell={cell}
                                      className={tableCellClassName}
                                    />
                                  )}
                                </SortableContext>
                              );
                            })}
                          </TableRow>
                        );

                        return renderRowContextMenu ? (
                          <Fragment key={row.id}>
                            {renderRowContextMenu(row, trigger)}
                          </Fragment>
                        ) : (
                          trigger
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          <Text variant="subHeading">
                            {noResultsMessage ?? "No results."}
                          </Text>
                        </TableCell>
                      </TableRow>
                    )}
                    {renderFooterRow?.(
                      table.getVisibleLeafColumns().map((column) => column.id),
                    )}
                  </TableBody>
                </>
              )}
            </Table>
            {shouldRenderStandaloneEmptyState ? (
              <div className="border-t p-6">{noResultsContent}</div>
            ) : null}
            {!isMatterDashboardChrome ? paginationContent : null}
          </div>
        </div>
        {isMatterDashboardChrome ? paginationContent : null}
      </div>
    </DndContext>
  );
}
